import { Component, OnDestroy, OnInit } from '@angular/core';
import { AccountInfo } from '@azure/msal-browser';
import { SchedulerItemChange, SchedulerViewName } from '@app/core/components/scheduler/scheduler.component';
import { ResponsiveService } from '@app/core/services/responsive.service';
import { RememberService } from '@app/core/services/remember.service';
import { DiaryOwner, DiaryPortalUser, DiaryService, DiarySetting, DiaryTask } from '@app/services/diary.service';
import { GraphAuthService } from '@app/modules/microsoft-graph/auth/services/graph-auth.service';
import { CalendarService } from '@app/modules/microsoft-graph/services/graph-calendar.service';
import { GraphUserService } from '@app/modules/microsoft-graph/services/graph-user.service';

@Component({
    selector: 'tt-diary',
    templateUrl: './diary.component.html',
    styleUrls: ['./diary.component.css'],
    standalone: false,
})
export class DiaryComponent implements OnInit, OnDestroy {
    public setting?: DiarySetting;
    public portalUsers: DiaryPortalUser[] = [];
    public tasks: DiaryTask[] = [];
    public owners: DiaryOwner[] = [];

    private taskTimer?: ReturnType<typeof setTimeout>;
    private ownerTimer?: ReturnType<typeof setTimeout>;
    private taskRequest = 0;
    private ownerRequest = 0;

    constructor(
        private diary: DiaryService,
        private responsive: ResponsiveService,
        private remember: RememberService,
        private graphAuth: GraphAuthService,
        private graphCalendar: CalendarService,
        private graphUser: GraphUserService,
    ) {}

    public async ngOnInit(): Promise<void> {
        const [setting, users] = await Promise.all([this.diary.getSetting(), this.diary.listPortalUsers()]);
        this.setting = { ...setting, breakpoint: this.responsive.current };
        this.portalUsers = users;
        this.scheduleLists();
    }

    public ngOnDestroy(): void {
        if (this.taskTimer) clearTimeout(this.taskTimer);
        if (this.ownerTimer) clearTimeout(this.ownerTimer);
    }

    public async onPortalUserChanged(value: string | number): Promise<void> {
        if (!this.setting || this.setting.portal_user_keyno === value) return;
        this.setting.portal_user_keyno = value;
        if (value !== '' && value !== null && value !== undefined) {
            await this.remember.remember('w_diary.portal_user_keyno', value);
        }
        this.scheduleLists();
    }

    public onSelectedDateChanged(value: string): void {
        if (this.setting) {
            this.setting.date_fom = value;
            this.scheduleLists();
        }
    }

    public onSelectedViewChanged(value: SchedulerViewName): void {
        if (this.setting) {
            this.setting.selectedview = value;
            this.scheduleLists();
        }
    }

    public async onTaskChanged(event: SchedulerItemChange<DiaryTask>): Promise<void> {
        const task = {
            ...event.item,
            start: event.currentValue.start,
            end: event.currentValue.end,
        };
        await this.diary.updateItem(task);
        await this.syncMicrosoftEvent(task);
    }

    public async onTaskRemoved(task: DiaryTask): Promise<void> {
        await this.diary.deleteCustact(task.id);
        if (task.microsoft_graph_event_id) {
            await this.graphCalendar.deleteEvent(task.microsoft_graph_event_id);
        }
    }

    private scheduleLists(): void {
        if (this.taskTimer) clearTimeout(this.taskTimer);
        if (this.ownerTimer) clearTimeout(this.ownerTimer);
        if (!this.setting || !this.hasValidFilters()) return;

        this.taskTimer = setTimeout(() => void this.loadTasks(), 50);
        this.ownerTimer = setTimeout(() => void this.loadOwners(), 50);
    }

    private hasValidFilters(): boolean {
        const setting = this.setting;
        return !!setting && setting.date_fom !== '' && setting.date_fom !== undefined && setting.date_fom !== null
            && setting.portal_user_keyno !== '' && setting.portal_user_keyno !== undefined && setting.portal_user_keyno !== null
            && setting.selectedview !== undefined && setting.selectedview !== null;
    }

    private async loadTasks(): Promise<void> {
        const setting = this.setting;
        const request = ++this.taskRequest;
        if (!setting || !this.hasValidFilters()) return;
        this.tasks = [];
        const tasks = await this.diary.listTasks(setting);
        if (request === this.taskRequest) this.tasks = tasks;
    }

    private async loadOwners(): Promise<void> {
        const setting = this.setting;
        const request = ++this.ownerRequest;
        if (!setting || !this.hasValidFilters()) return;
        this.owners = [];
        const owners = await this.diary.listOwners(setting);
        if (request === this.ownerRequest) this.owners = owners;
    }

    private async syncMicrosoftEvent(task: DiaryTask): Promise<void> {
        if (!task.id || task.id === 0 || task.sync_with_microsoft_365 !== '1' || !task.microsoft_graph_event_id) return;
        try {
            const account: AccountInfo = await this.graphAuth.getAccount();
            const token = await this.graphAuth.getAccessToken(account);
            await this.graphUser.getUser(account.localAccountId, token);
            await this.graphCalendar.updateEvent({
                start: { dateTime: this.formatLocalDateTime(task.start), timeZone: 'Europe/Berlin' },
                end: { dateTime: this.formatLocalDateTime(task.end), timeZone: 'Europe/Berlin' },
            }, { eventId: task.microsoft_graph_event_id, token });
        } catch (error) {
            console.error('Unable to sync diary event with Microsoft 365.', error);
        }
    }

    private formatLocalDateTime(value: Date | string | undefined): string {
        const date = value instanceof Date ? value : new Date(value ?? '');
        const pad = (part: number) => String(part).padStart(2, '0');
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    }
}
